import React from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Card,
  Spin,
  Modal,
  Select,
  Form,
  DatePicker,
  Tooltip,
  message,
  Upload,
  Checkbox,
} from "antd";
import { useNavigate } from "react-router-dom";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { RiAddBoxFill } from "react-icons/ri";
import { FaEdit, FaPen, FaTrashAlt } from "react-icons/fa";
import { AiFillDelete } from "react-icons/ai";
import {
  createHoliday,
  getAllHolidays,
  deleteHoliday,
  bulkAddHoliday,
} from "../../utils/API";
import { HiPencilAlt } from "react-icons/hi";
import Editholidaymodal from "./Edit";
import type { ColumnsType } from "antd/es/table";
import * as XLSX from "xlsx";
import type { SortOrder } from "antd/es/table/interface";

const { Title } = Typography;
const { Option } = Select;

interface Holiday {
  key: string;
  date: string;
  desc?: string;
  checkbox?: any;
  _id: string;
  type?: string;
  year?: number;
  selecttype?: string;
}

const Holidays = () => {
  const [searchText, setSearchText] = React.useState<string>("");
  // const [isPermissionModalVisible, setIsPermissionModalVisible] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isPermissionModalVisible, setIsPermissionModalVisible] =
    React.useState(false);
  const [holidayToDelete, setHolidayToDelete] = React.useState<string | null>(
    null,
  );
  const [selectedYear, setSelectedYear] = React.useState<number>(
    new Date().getFullYear(),
  );
  const [holidayType, setHolidayType] = React.useState<string | undefined>(
    undefined,
  );
  const [isAddHolidayModalVisible, setIsAddHolidayModalVisible] =
    React.useState(false);
  const [form] = Form.useForm();
  const [currentTask, setCurrentTask] = useState<Holiday | null>(null);
  // const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(1);
  const [dataSource, setDataSource] = React.useState<Holiday[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: SortOrder;
  }>({
    field: "date",
    direction: "descend",
  });
  const [allowPastDates, setAllowPastDates] = useState(false);

  // Add this handler function
  const handleAllowPastDatesChange = (e: any) => {
    setAllowPastDates(e.target.checked);
  };
  const [isEditModalVisible, setIsEditModalVisible] = React.useState(false);
  // Duplicate declaration removed
  const navigate = useNavigate();
  const [totalRecords, setTotalRecords] = React.useState<number>(0);
  const fetchHolidays = async (
    sortField: string = sortConfig.field,
    sortOrder: SortOrder = sortConfig.direction,
  ) => {
    try {
      setLoading(true);

      const params = {
        page,
        year: selectedYear,
        type: holidayType,
        search: searchText,
        sortField, // Add sort field to params
        sortOrder, // Add sort order to params
      };

      type HolidaysApiResponse = {
        data: any[] | { holidays?: any[]; holiday?: any };
        total?: number;
      };
      const onClose = () => {
        setIsAddHolidayModalVisible(false);
        form.resetFields(); // Also reset the form when closing
      };
      const response: HolidaysApiResponse = await getAllHolidays(params);

      const holidaysData = Array.isArray(response.data)
        ? response.data
        : response.data.holidays
          ? response.data.holidays
          : response.data.holiday
            ? [response.data.holiday]
            : [];

      const formattedData = holidaysData.map((holiday: any, index: number) => ({
        key: ((response.total || sortedData.length) - index).toString(),
        date: holiday.date,
        desc: holiday.desc,
        _id: holiday._id,
        type: holiday.type,
      }));

      // Client-side sorting fallback if API doesn't support sorting
      let sortedData: Holiday[] = formattedData;
      if (sortField === "date" && sortOrder) {
        sortedData = [...formattedData].sort((a: Holiday, b: Holiday) => {
          const dateA = dayjs(a.date, "DD-MM-YYYY");
          const dateB = dayjs(b.date, "DD-MM-YYYY");
          return sortOrder === "ascend" ? dateA.diff(dateB) : dateB.diff(dateA);
        });
      }

      setDataSource(sortedData);
      setTotalRecords(response.total || sortedData.length);
    } catch (error) {
      console.error("Error fetching holidays:", error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchHolidays();
  }, [selectedYear, holidayType, page, searchText]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    // Handle pagination changes
    if (pagination.current !== page) {
      setPage(pagination.current);
    }

    // Handle sorting changes
    if (sorter.field) {
      setSortConfig({
        field: sorter.field,
        direction: sorter.order,
      });
      fetchHolidays(sorter.field, sorter.order);
    }
  };
  const handleDeleteConfirmation = (id: string) => {
    setHolidayToDelete(id);
    setIsPermissionModalVisible(true);
  };
  const handleEdit = (record: Holiday) => {
    setCurrentTask(record);
    setIsEditModalVisible(true);
  };

  const handleActualDelete = async () => {
    if (holidayToDelete) {
      try {
        setLoading(true);
        await deleteHoliday(holidayToDelete);
        await fetchHolidays();
      } catch (error) {
        console.error("Error deleting holiday:", error);
      } finally {
        setIsPermissionModalVisible(false);
        setHolidayToDelete(null);
        setLoading(false);
      }
    }
  };

  const showAddHolidayModal = () => {
    setIsAddHolidayModalVisible(true);
  };
  function handleSubmit(_id: string) {
    throw new Error("Function not implemented.");
  }
  // In handleAddHolidayOk
  const handleAddHolidaySubmit = async (values: any) => {
    try {
      setIsLoading(true);

      const today = dayjs().startOf("day");
      const selectedDate = dayjs(values.date).startOf("day");

      // Get the checkbox value from form values instead of separate state
      const allowPastDates = values.allowPastDates || false;

      // Only validate if checkbox is not checked
      if (!allowPastDates && selectedDate.isBefore(today)) {
        message.error("Cannot add holidays for past dates");
        return;
      }

      const holidayData = {
        date: selectedDate.format("DD-MM-YYYY"),
        desc: values.desc,
        type: values.type,
        allowPastDates: allowPastDates,
      };

      await createHoliday(holidayData);
      message.success("Holiday added successfully");
      setIsAddHolidayModalVisible(false);
      form.resetFields();
      fetchHolidays();
    } catch (error) {
      console.error("Error creating holiday:", error);
      message.error("Failed to add holiday");
    } finally {
      setIsLoading(false);
    }
  };

  // In your edit modal, ensure you parse the date correctly when receiving it:
  useEffect(() => {
    if (currentTask) {
      form.setFieldsValue({
        ...currentTask,
        date: dayjs(currentTask.date, "DD-MM-YYYY"), // Parse the stored date
      });
    }
  }, [currentTask, form]);

  const handleBulkUpload = async (file: any) => {
    setImportLoading(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData: any[] = XLSX.utils.sheet_to_json(sheet);

        if (jsonData.length === 0) {
          message.warning("Empty file - no data found.");
          return;
        }

        // Validate the Excel structure
        const requiredFields = ["date", "desc", "type"];
        const firstRow = jsonData[0];
        const missingFields = requiredFields.filter(
          (field) => !(field in firstRow),
        );

        if (missingFields.length > 0) {
          message.error(
            `Missing required columns in Excel: ${missingFields.join(", ")}`,
          );
          return;
        }

        // Transform data to match API expectations
        const formattedData = jsonData.map((item) => {
          let parsedDate;

          if (
            dayjs(
              item.date,
              ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", "DD-MM-YYYY"],
              true,
            ).isValid()
          ) {
            parsedDate = dayjs(item.date, [
              "DD/MM/YYYY",
              "MM/DD/YYYY",
              "YYYY-MM-DD",
              "DD-MM-YYYY",
            ]).format("DD-MM-YYYY");
          } else if (typeof item.date === "number") {
            const parsedExcelDate = XLSX.SSF.parse_date_code(item.date);
            if (parsedExcelDate) {
              const { y, m, d } = parsedExcelDate;
              parsedDate = dayjs(`${y}-${m}-${d}`, "YYYY-M-D").format(
                "DD-MM-YYYY",
              );
            } else {
              parsedDate = "Invalid Date";
            }
          } else {
            parsedDate = "Invalid Date";
          }

          return {
            date: parsedDate,
            desc: item.desc,
            type: item.type,
          };
        });

        const response = await bulkAddHoliday(formattedData);

        if (response.success) {
          const inserted = response.insertedCount || 0;
          const duplicates = response.duplicateCount || 0;
          const invalid = response.invalidCount || 0;

          let successMsg = `Successfully imported ${inserted} holidays.`;
          if (duplicates > 0)
            successMsg += ` ${duplicates} duplicates skipped.`;
          if (invalid > 0) successMsg += ` ${invalid} invalid records skipped.`;

          message.success(successMsg);
          fetchHolidays();
        } else {
          message.error(response.message || "Bulk upload failed");
        }
      } catch (error) {
        console.error("Bulk upload error:", error);
        message.error(
          "Failed to process file. Please check the format and try again.",
        );
      } finally {
        setImportLoading(false);
      }
    };

    reader.onerror = () => {
      message.error("Error reading file");
      setImportLoading(false);
    };

    reader.readAsArrayBuffer(file);
  };
  const columns: ColumnsType<Holiday> = [
    {
      title: "#",
      dataIndex: "key",
      key: "key",
      render: (text: string, record: Holiday, index: number) => (
        <span>{(page - 1) * 10 + index + 1}</span>
      ),
    },
    {
      title: "Description",
      dataIndex: "desc",
      key: "desc",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => dayjs(date, "DD-MM-YYYY").format("DD-MM-YYYY"),
      sorter: true,
      sortDirections: ["ascend", "descend"] as SortOrder[],
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Action",
      key: "action",
      render: (_text: any, record: Holiday) => (
        <div style={{ display: "flex", gap: "8px" }}>
          {/* edit icon */}
          <Tooltip title="Edit">
            <button
              onClick={() => handleEdit(record)}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
               shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
               hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
               active:translate-y-[1px] active:shadow-none 
               text-white flex items-center justify-center 
               transition-all duration-200 ease-in-out group"
            >
              <FaPen
                className="group-hover:scale-110 transition-transform duration-200"
                size={12}
              />
            </button>
          </Tooltip>
          {/* Delete Button */}
          <Tooltip title="Delete">
            <button
              onClick={() => handleDeleteConfirmation(record._id)}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 
               shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
               hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
               active:translate-y-1 active:shadow-none 
               text-white flex items-center justify-center 
               transition-all duration-200 group"
            >
              <FaTrashAlt
                className="group-hover:scale-110 transition-transform"
                size={12}
              />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];
  useEffect(() => {
    if (isAddHolidayModalVisible) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
  }, [isAddHolidayModalVisible]);

  return (
    <div>
      <Row className="m-2" align="middle" gutter={[16, 16]} wrap={false}>
        {/* Left - Heading */}
        <Col xs={4} sm={4} md={3} lg={3} xl={4}>
          <h2 className="headername">Holidays</h2>
        </Col>

        {/* Middle - Filters */}
        <Col xs={8} sm={8} md={8} lg={9} xl={9}>
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <Select
                placeholder="Select Holiday Type"
                value={holidayType}
                onChange={setHolidayType}
                style={{ width: "100%", height: "33px" }}
                allowClear
              >
                <Option value="Holiday">Holiday</Option>
                <Option value="WeekOff">WeekOff</Option>
              </Select>
            </Col>
            <Col span={12}>
              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                options={Array.from({ length: 20 }, (_, i) => {
                  const year = dayjs().year() - 10 + i;
                  return { value: year, label: year.toString() };
                })}
                style={{ width: "100%", height: "33px" }}
              />
            </Col>
          </Row>
        </Col>

        {/* Right - Search + Buttons */}
        <Col xs={12} sm={12} md={13} lg={12} xl={11}>
          <div className="flex items-center justify-end gap-2 w-full">
            <Input
              prefix={<SearchOutlined />}
              size="large"
              placeholder="Search..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="flex-1"
              style={{ height: "33px", minWidth: "150px" }}
            />

            <Button
              type="primary"
              onClick={showAddHolidayModal}
              className="allbuttons"
              style={{ height: "33px" }}
            >
              <RiAddBoxFill className="button-icon" />
              <span className="button-text">Add Holiday</span>
            </Button>

            {/* <Button
        onClick={exportToExcel}
        style={{
          background: "#001529",
          color: "white",
          border: "none",
          height: "33px",
          minWidth: "100px",
        }}
        disabled={exportLoading}
      >
        {exportLoading ? "Exporting..." : "Export Data"}
      </Button> */}
            <Upload
              name="file"
              accept=".xlsx, .xls, .csv"
              showUploadList={false}
              beforeUpload={(file: any) => {
                handleBulkUpload(file);
                return false; // Prevent auto-upload
              }}
            >
              <Button
                style={{
                  background: "#001529",
                  color: "white",
                  border: "none",
                  height: "33px",
                  minWidth: "100px",
                }}
                disabled={importLoading}
              >
                {importLoading ? "Importing..." : "Import Data"}
              </Button>
            </Upload>
          </div>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Card className=" w-100">
            <Spin spinning={loading}>
              <div className="scrollbar-hide custom-scroll-table">
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  loading={loading}
                  onChange={handleTableChange} // Add this line
                  scroll={{ x: "max-content" }}
                  rowKey={(record) => record._id}
                  pagination={{
                    pageSize: 10,
                    current: page,
                    onChange: setPage,
                    showSizeChanger: false,
                    total: totalRecords,
                  }}
                />
              </div>
            </Spin>
          </Card>
        </Col>
      </Row>

      <Modal
        visible={isPermissionModalVisible}
        onOk={handleActualDelete}
        onCancel={() => setIsPermissionModalVisible(false)}
        okText="Yes"
        cancelText="No"
        // footer={null}
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Holiday?</p>
      </Modal>

      <Modal
        open={isAddHolidayModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsAddHolidayModalVisible(false);
        }}
        className="custom-modal-with-borders"
        width={550}
        maskClosable={false}
        footer={null} // ❌ Remove default footer to use custom one inside Form
        bodyStyle={{
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        style={{
          bottom: 20,
          top: 20,
          margin: "0 auto",
          position: "relative",
        }}
        styles={{
          header: {
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "#f1f5ffff",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
        }}
        title={
          <div
            style={{
              color: "#4b5563",
              fontFamily: "inter, sans-serif",
              fontSize: "20px",
              fontWeight: 500,
            }}
          >
            Add
          </div>
        }
      >
        <Form
          form={form}
          onFinish={handleAddHolidaySubmit} // Use the new handler here
          layout="horizontal"
          labelCol={{ span: 5 }}
          wrapperCol={{ span: 19 }}
          colon={false}
        >
          <div className="elements">
            {/* Type */}

            <Form.Item
              label={
                <span
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "14px",
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  Type <span className="text-danger">*</span>
                </span>
              }
            >
              <Form.Item
                name="type"
                rules={[{ required: true, message: "Please select a type" }]}
              >
                <Select
                  className="CustomSelect"
                  placeholder="Select type"
                  options={[
                    { value: "Holiday", label: "Holiday" },
                    { value: "WeekOff", label: "WeekOff" },
                  ]}
                />
              </Form.Item>
            </Form.Item>

            {/* Date */}
            <Form.Item
              label={
                <span
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "14px",
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  {" "}
                  Date <span className="text-danger">*</span>
                </span>
              }
            >
              <Form.Item
                name="date"
                rules={[{ required: true, message: "Please select a date" }]}
              >
                <DatePicker format="DD/MM/YYYY" className="Allinput" />
              </Form.Item>
            </Form.Item>

            {/* Description */}
            <Form.Item
              label={
                <span
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "14px",
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  {" "}
                  Description <span className="text-danger">*</span>
                </span>
              }
            >
              <Form.Item
                name="desc"
                rules={[
                  { required: true, message: "Please enter a description" },
                ]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="E.g., New Year's Day"
                  className="Allinputtextarea"
                />
              </Form.Item>
            </Form.Item>
            {/* Allow Past Dates Checkbox */}
            <Form.Item>
              <Form.Item name="allowPastDates" valuePropName="checked" noStyle>
                <Checkbox
                  onChange={handleAllowPastDatesChange}
                  checked={allowPastDates}
                >
                  Allow adding holidays for past dates
                </Checkbox>
              </Form.Item>
              {/* <div className="text-muted" style={{ fontSize: '12px', marginTop: '4px' }}>
                Check this if you want to add holidays that have already passed
              </div> */}
            </Form.Item>
          </div>
          {/* Buttons in Footer */}
          <Row justify="end">
            <Col
              xs={24}
              sm={24}
              md={24}
              xl={24}
              xxl={20}
              className="d-flex justify-content-end me-3"
            >
              <div className="button-wrapper">
                <button
                  className="allcanclebutton"
                  onClick={() => {
                    form.resetFields();
                    setIsAddHolidayModalVisible(false);
                  }}
                  disabled={isLoading}
                  type="button"
                >
                  Cancel
                </button>

                <button
                  className="allsubmitebutton"
                  disabled={isLoading}
                  type="submit" // ✅ Very important: triggers validation
                >
                  {isLoading ? "Add Holiday..." : "Add Holiday"}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>

      {currentTask && (
        // In your parent component where you render EditCustomersModal:
        <Editholidaymodal
          visible={isEditModalVisible}
          customerId={currentTask?._id}
          onClose={() => {
            setIsEditModalVisible(false);
            setCurrentTask(null);
          }}
          onSuccess={() => {
            fetchHolidays();
          }}
        />
      )}
    </div>
  );
};

export const HOLIDAYS_TABLE_COLUMNS = ["desc", "date", "type"];

export default Holidays;
